import { SmartCardRow } from "@/domains/database/repositories/smart-card-repository";
import { CollectionListSmartCardRenderer } from "@/components/heads-up/cards/collection-list-smart-card-renderer";
import { EventTimelineSmartCardRenderer } from "@/components/heads-up/cards/event-timeline-smart-card-renderer";
import { MarkdownSmartCardRenderer } from "@/components/heads-up/cards/markdown-smart-card-renderer";
import { MentionedByNotesListSmartCardRenderer } from "@/components/heads-up/cards/mentioned-by-notes-list-smart-card-renderer";
import { NoteListSmartCardRenderer } from "@/components/heads-up/cards/note-list-smart-card-renderer";
import type { MouseEvent } from "react";
import type { CollectionListSmartCardCollectionInfo } from "./collection-list-smart-card.types";
import type { SmartNotePreviewInfo } from "@/components/heads-up/cards/common/smart-note-preview.types";
import type { Maybe } from "@/domains/common/types";
import {
  SmartCard,
  SmartCardKindValues,
  SmartCardKind as SmartCardKindEnum,
  NoteListSmartCard,
  MentionedByNotesListSmartCard,
  CollectionListSmartCard,
  EventTimelineSmartCard,
  MarkdownSmartCard,
} from "@/components/heads-up/heads-up.types";

export function toTypedSmartCard(row: SmartCardRow): SmartCard | null {
  const kind = row.derived_kind as SmartCardKindValues | null;
  const value = row.value_json as Record<string, unknown> | null;
  if (!kind || !value) return null;

  switch (kind) {
    case SmartCardKindEnum.NoteList: {
      const typed: NoteListSmartCard = {
        id: row.id,
        smart_view_id: row.smart_view_id,
        sort_key: row.sort_key ?? "",
        kind: SmartCardKindEnum.NoteList,
        copied_from_smart_card_id: null,
        originated_from_find_more: row.originated_from_find_more ?? false,
        value: {
          title: (value.title as string) ?? "",
          items: (Array.isArray(value.items)
            ? value.items
            : []) as NoteListSmartCard["value"]["items"],
        },
      };
      return typed;
    }
    case SmartCardKindEnum.MentionedByNotesList: {
      const typed: MentionedByNotesListSmartCard = {
        id: row.id,
        smart_view_id: row.smart_view_id,
        sort_key: row.sort_key ?? "",
        kind: SmartCardKindEnum.MentionedByNotesList,
        copied_from_smart_card_id: null,
        originated_from_find_more: row.originated_from_find_more ?? false,
        value: {
          title: (value.title as string) ?? "",
          items: (Array.isArray(value.items)
            ? value.items
            : []) as MentionedByNotesListSmartCard["value"]["items"],
        },
      };
      return typed;
    }
    case SmartCardKindEnum.CollectionList: {
      const typed: CollectionListSmartCard = {
        id: row.id,
        smart_view_id: row.smart_view_id,
        sort_key: row.sort_key ?? "",
        kind: SmartCardKindEnum.CollectionList,
        copied_from_smart_card_id: null,
        originated_from_find_more: row.originated_from_find_more ?? false,
        value: {
          title: (value.title as string) ?? "",
          items: (Array.isArray(value.items)
            ? value.items
            : []) as CollectionListSmartCard["value"]["items"],
        },
      };
      return typed;
    }
    case SmartCardKindEnum.EventTimeline: {
      const typed: EventTimelineSmartCard = {
        id: row.id,
        smart_view_id: row.smart_view_id,
        sort_key: row.sort_key ?? "",
        kind: SmartCardKindEnum.EventTimeline,
        copied_from_smart_card_id: null,
        originated_from_find_more: row.originated_from_find_more ?? false,
        value: {
          title: (value.title as string) ?? "",
          items: (Array.isArray(value.items)
            ? value.items
            : []) as EventTimelineSmartCard["value"]["items"],
        },
      };
      return typed;
    }
    case SmartCardKindEnum.Markdown: {
      const typed: MarkdownSmartCard = {
        id: row.id,
        smart_view_id: row.smart_view_id,
        sort_key: row.sort_key ?? "",
        kind: SmartCardKindEnum.Markdown,
        copied_from_smart_card_id: null,
        originated_from_find_more: row.originated_from_find_more ?? false,
        value: {
          title: (value.title as string) ?? "",
          markdown: (value.markdown as string | null) ?? null,
        },
      };
      return typed;
    }
    default:
      return null;
  }
}

export interface RenderSmartCardOptions {
  isHoverable?: boolean;
  getNotePreviewInfo?: (noteId: string) => Maybe<SmartNotePreviewInfo>;
  getCollectionInfo?: (collectionId: string) => Maybe<CollectionListSmartCardCollectionInfo>;
  onCollectionClick?: (collectionId: string, event: MouseEvent<HTMLButtonElement>) => void;
}

export function renderSmartCard(
  card: SmartCardRow,
  options: RenderSmartCardOptions = {}
): JSX.Element | null {
  const typed = toTypedSmartCard(card);
  if (!typed) return null;

  switch (typed.kind) {
    case SmartCardKindEnum.NoteList:
      return (
        <NoteListSmartCardRenderer card={typed} getNotePreviewInfo={options.getNotePreviewInfo} />
      );
    case SmartCardKindEnum.MentionedByNotesList:
      return (
        <MentionedByNotesListSmartCardRenderer
          card={typed}
          getNotePreviewInfo={options.getNotePreviewInfo}
        />
      );
    case SmartCardKindEnum.CollectionList:
      return (
        <CollectionListSmartCardRenderer
          card={typed}
          getCollectionInfo={options.getCollectionInfo}
          onCollectionClick={options.onCollectionClick}
        />
      );
    case SmartCardKindEnum.EventTimeline:
      return (
        <EventTimelineSmartCardRenderer
          card={typed}
          getNotePreviewInfo={options.getNotePreviewInfo}
        />
      );
    case SmartCardKindEnum.Markdown:
      return <MarkdownSmartCardRenderer card={typed} />;
    default:
      return null;
  }
}
